import { getFormattedDate } from '@/src/utils/date'
import { isInternalUser, isUserSignedIn } from '@/src/utils/generalUtils/checkUserLogin'

export type MulticityUrlSegment = {
    from: { airportCode?: string; value?: string | null }
    to: { airportCode?: string; value?: string | null }
    date: Date | null
}

export type BuildMulticitySearchUrlInput = {
    segments: MulticityUrlSegment[]
    adults: number
    childs: number
    infants: number
    flightClass: string
    locale: string
    maxSegments?: number
    isNonStop?: boolean
    /** Used for the new SRP URL when already on SRP (defaults to `/${locale}/flights/results`). */
    newUrlPathname?: string
}

const CABIN_MAP: Record<string, string> = {
    Economy: 'e',
    Business: 'b',
    First: 'f'
}

const randomString = (): string => {
    let text = ''
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    for (let i = 0; i < 12; i++) {
        text += possible.charAt(Math.floor(Math.random() * possible.length))
    }
    return text
}

/** New multicity SRP is enabled only for signed-in internal users (@cleartrip / @flyin). */
export const shouldUseNewMulticityUrl = (): boolean => isUserSignedIn() && isInternalUser()

export const buildNewMulticitySearchUrl = ({
    segments,
    adults,
    childs,
    infants,
    flightClass,
    locale,
    isNonStop,
    newUrlPathname
}: BuildMulticitySearchUrlInput): string => {
    const queryParams = new URLSearchParams()

    segments.forEach((segment, index) => {
        const segmentNum = index + 1
        queryParams.set(`dep${segmentNum}`, segment.from.airportCode || '')
        queryParams.set(`arr${segmentNum}`, segment.to.airportCode || '')
        queryParams.set(`depdate${segmentNum}`, segment.date ? getFormattedDate(segment.date, 'DD/MM/YYYY') : '')
        queryParams.set(`origin${segmentNum}`, (segment.from.value as string) || '')
        queryParams.set(`destination${segmentNum}`, (segment.to.value as string) || '')
    })

    queryParams.set('adults', adults.toString())
    queryParams.set('childs', childs.toString())
    queryParams.set('infants', infants.toString())
    queryParams.set('class', flightClass)

    if (isNonStop) {
        queryParams.set('nonstop', '0')
    }

    const pathname = newUrlPathname ?? `/${locale}/flights/results`
    return `${pathname}?${queryParams.toString()}`
}

export const buildLegacyMulticitySearchUrl = ({
    segments,
    adults,
    childs,
    infants,
    flightClass,
    maxSegments = 6,
    isNonStop
}: BuildMulticitySearchUrlInput): string => {
    const queryParams = new URLSearchParams()

    queryParams.set('tripType', '3')

    segments.forEach((segment, index) => {
        const segmentNum = index + 1
        queryParams.set(`dep${segmentNum}`, segment.from.airportCode || '')
        queryParams.set(`arr${segmentNum}`, segment.to.airportCode || '')
        queryParams.set(`depdate${segmentNum}`, segment.date ? getFormattedDate(segment.date, 'YYYY-MM-DD') : '')
    })

    for (let i = segments.length + 1; i <= maxSegments; i++) {
        queryParams.set(`dep${i}`, '')
        queryParams.set(`arr${i}`, '')
        queryParams.set(`depdate${i}`, '')
    }

    queryParams.set('adt', adults.toString())
    queryParams.set('chd', childs.toString())
    queryParams.set('inf', infants.toString())

    const cbn = CABIN_MAP[flightClass] || flightClass.charAt(0).toLowerCase()
    queryParams.set('cbn', cbn)
    queryParams.set('airlineType', '')
    queryParams.set('isDirectFlights', 'false')

    if (isNonStop) {
        queryParams.set('nonstop', '0')
    }

    if (segments.length === 2) {
        queryParams.set('token', randomString())
    }

    return `/flight/result?${queryParams.toString()}`
}

export const buildMulticitySearchUrl = (input: BuildMulticitySearchUrlInput): string => {
    if (shouldUseNewMulticityUrl()) {
        return buildNewMulticitySearchUrl(input)
    }
    return buildLegacyMulticitySearchUrl(input)
}
